import { useEffect, useState } from 'react';

export type DesktopLayoutState = {
  isSidebarOpen: boolean;
  isNotificationPanelOpen: boolean;
};

export type DesktopLayoutActions = {
  toggleSidebar: () => void;
  closeSidebar: () => void;
  openSidebar: () => void;
  toggleNotificationPanel: () => void;
  closeNotificationPanel: () => void;
  openNotificationPanel: () => void;
};

export type UseDesktopLayoutReturn = DesktopLayoutState & DesktopLayoutActions;

const SIDEBAR_STORAGE_KEY = 'desktop-layout-sidebar-state';
const NOTIFICATION_PANEL_STORAGE_KEY = 'desktop-layout-notification-panel-state';

/**
 * Custom hook for managing desktop layout state including sidebar and notification panel
 * Provides session persistence for both sidebar and notification panel state
 */
export const useDesktopLayout = (): UseDesktopLayoutReturn => {
  // Initialize sidebar state from localStorage or default to true for desktop
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  // Initialize notification panel state from localStorage or default to false
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(NOTIFICATION_PANEL_STORAGE_KEY);
      return stored !== null ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // Persist sidebar state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(isSidebarOpen));
    } catch (error) {
      console.warn('Failed to persist sidebar state:', error);
    }
  }, [isSidebarOpen]);

  // Persist notification panel state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(NOTIFICATION_PANEL_STORAGE_KEY, JSON.stringify(isNotificationPanelOpen));
    } catch (error) {
      console.warn('Failed to persist notification panel state:', error);
    }
  }, [isNotificationPanelOpen]);

  // Sidebar actions
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const openSidebar = () => {
    setIsSidebarOpen(true);
  };

  // Notification panel actions
  const toggleNotificationPanel = () => {
    setIsNotificationPanelOpen((prev) => !prev);
  };

  const closeNotificationPanel = () => {
    setIsNotificationPanelOpen(false);
  };

  const openNotificationPanel = () => {
    setIsNotificationPanelOpen(true);
  };

  return {
    // State
    isSidebarOpen,
    isNotificationPanelOpen,
    // Actions
    toggleSidebar,
    closeSidebar,
    openSidebar,
    toggleNotificationPanel,
    closeNotificationPanel,
    openNotificationPanel
  };
};
